import { useEffect, useRef, useState } from "react";

const AudioItem = ({ item }) => {
  const audioRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);

  const audioUrl = item?.file_url
    ? `https://admin-service.swasthyakhata.com${item.file_url}`
    : "";

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || 0);
    const onEnded = () => setPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.src = audioUrl;
    audio.load();

    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioUrl]);

  const formatTime = (time) => {
    if (!Number.isFinite(time)) return "0:00";
    return `${Math.floor(time / 60)}:${String(
      Math.floor(time % 60)
    ).padStart(2, "0")}`;
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (audio.paused) {
      try {
        await audio.play();
        setPlaying(true);
      } catch (error) {
        setPlaying(false);
        console.error("Audio playback failed:", error);
      }
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const seek = (value) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;

    audio.currentTime = (Number(value) / 100) * audio.duration;
    setCurrentTime(audio.currentTime);
  };

  const changeVolume = (value) => {
    const nextVolume = Number(value);
    setVolume(nextVolume);
    setMuted(nextVolume === 0);

    if (audioRef.current) {
      audioRef.current.volume = nextVolume;
      audioRef.current.muted = nextVolume === 0;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const nextMuted = !muted;
    audio.muted = nextMuted;
    setMuted(nextMuted);

    if (!nextMuted && volume === 0) {
      audio.volume = 1;
      setVolume(1);
    }
  };

  const changeSpeed = () => {
    const speeds = [1, 1.25, 1.5, 1.75, 2, 0.75];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];

    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(
      0,
      Math.min(audio.duration || 0, audio.currentTime + seconds)
    );
  };

  const downloadAudio = () => {
    if (!audioUrl) return;

    const link = document.createElement("a");
    link.href = audioUrl;
    link.download = `${item?.title || "audio-recording"}.mp3`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const buttonStyle = {
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "10px",
    minWidth: "34px",
    height: "34px",
    padding: "0 7px",
    background: "rgba(255,255,255,0.07)",
    color: "#e2e8f0",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    flexShrink: 0,
  };

  return (
    <div
      style={{
        width: "min(100%, 340px)",
        minWidth: 0,
        minHeight: "fit-content",
        boxSizing: "border-box",
        borderRadius: "18px",
        padding: "12px",
        margin: "10px",
        background: "linear-gradient(145deg, #ffffff, #eef2f7)",
        border: "1px solid #dce3ec",
        boxShadow: "0 8px 24px rgba(15,23,42,0.09)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        position: "relative",
        overflow: "hidden",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {/* Cover image */}
      <div
        style={{
          width: "100%",
          height: "155px",
          borderRadius: "13px",
          overflow: "hidden",
          position: "relative",
          background: "#172554",
        }}
      >
        <img
          src="/Assets/music-bg.avif"
          alt="Audio cover"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, rgba(15,23,42,0.65), transparent 70%)",
          }}
        />

        <div
          style={{
            position: "absolute",
            left: "12px",
            bottom: "12px",
            color: "#fff",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.2px",
            textTransform: "uppercase",
          }}
        >
          AUDIO LIBRARY
        </div>

        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            padding: "5px 9px",
            borderRadius: "20px",
            color: "#fff",
            fontSize: "10px",
            fontWeight: 600,
            background: "rgba(15,23,42,0.65)",
            backdropFilter: "blur(8px)",
          }}
        >
          {playing ? "● PLAYING" : "♫ AUDIO"}
        </div>
      </div>

      {/* Item information */}
      <div style={{ width: "100%", minWidth: 0 }}>
        <p
          title={item?.artist || ""}
          style={{
            margin: "0 0 5px",
            color: "#64748b",
            fontSize: "12px",
            fontWeight: 600,
            overflowWrap: "anywhere",
          }}
        >
          {item?.artist || "Unknown Artist"}
        </p>

        <p
          title={item?.title || ""}
          style={{
            margin: 0,
            color: "#172033",
            fontSize: "19px",
            lineHeight: 1.35,
            fontWeight: 750,
            overflowWrap: "anywhere",
          }}
        >
          {item?.title || "Untitled Audio"}
        </p>
      </div>

      {/* Audio player */}
      <div
        style={{
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          padding: "15px 12px",
          borderRadius: "15px",
          background:
            "linear-gradient(135deg, #111827 0%, #1e293b 60%, #172554 100%)",
          color: "#f8fafc",
          boxShadow: "0 5px 14px rgba(15,23,42,0.12)",
        }}
      >
        <audio
          ref={audioRef}
          src={audioUrl || undefined}
          preload="metadata"
          style={{ display: "none" }}
        />

        {/* Progress */}
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={progress}
          onChange={(e) => seek(e.target.value)}
          aria-label="Seek audio"
          style={{
            display: "block",
            width: "100%",
            height: "5px",
            margin: "0 0 8px",
            cursor: "pointer",
            accentColor: "#38bdf8",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "#94a3b8",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Playback controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            marginTop: "13px",
          }}
        >
          <button
            type="button"
            style={buttonStyle}
            onClick={() => skip(-10)}
            title="Rewind 10 seconds"
            aria-label="Rewind 10 seconds"
          >
            ↶10
          </button>

          <button
            type="button"
            onClick={togglePlay}
            disabled={!audioUrl}
            title={playing ? "Pause" : "Play"}
            aria-label={playing ? "Pause audio" : "Play audio"}
            style={{
              width: "47px",
              height: "47px",
              flexShrink: 0,
              border: "none",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #38bdf8, #818cf8)",
              color: "#0f172a",
              fontSize: "20px",
              cursor: audioUrl ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: audioUrl ? 1 : 0.5,
              boxShadow: "0 4px 15px rgba(56,189,248,0.2)",
            }}
          >
            {playing ? "Ⅱ" : "▶"}
          </button>

          <button
            type="button"
            style={buttonStyle}
            onClick={() => skip(10)}
            title="Forward 10 seconds"
            aria-label="Forward 10 seconds"
          >
            10↷
          </button>
        </div>

        {/* Volume, speed and download */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "14px",
            paddingTop: "12px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              flex: "1 1 100px",
              minWidth: 0,
            }}
          >
            <button
              type="button"
              style={buttonStyle}
              onClick={toggleMute}
              aria-label={muted ? "Unmute audio" : "Mute audio"}
              title={muted ? "Unmute" : "Mute"}
            >
              {muted || volume === 0 ? "🔇" : "🔊"}
            </button>

            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => changeVolume(e.target.value)}
              aria-label="Volume"
              style={{
                width: "100%",
                minWidth: "35px",
                maxWidth: "90px",
                accentColor: "#38bdf8",
                cursor: "pointer",
              }}
            />
          </div>

          <button
            type="button"
            style={buttonStyle}
            onClick={changeSpeed}
            title="Change playback speed"
            aria-label={`Playback speed ${speed}x`}
          >
            {speed}x
          </button>

          <button
            type="button"
            style={buttonStyle}
            onClick={downloadAudio}
            title="Download audio"
            aria-label="Download audio"
          >
            ↓
          </button>
        </div>
      </div>

      {/* Optional assigned doctors */}
      {Array.isArray(item?.AssignedDoc) && item.AssignedDoc.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            width: "100%",
          }}
        >
          {item.AssignedDoc.map((doc, index) => (
            <span
              key={`${doc}-${index}`}
              style={{
                padding: "5px 8px",
                borderRadius: "7px",
                background: "#dbeafe",
                color: "#1e40af",
                fontSize: "11px",
                overflowWrap: "anywhere",
              }}
            >
              {doc}
            </span>
          ))}
        </div>
      )}

      {/* Optional disease tags */}
      {Array.isArray(item?.disease) && item.disease.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            width: "100%",
          }}
        >
          {item.disease.map((disease, index) => (
            <span
              key={`${disease}-${index}`}
              style={{
                padding: "5px 8px",
                borderRadius: "7px",
                background: "#dcfce7",
                color: "#166534",
                fontSize: "11px",
                overflowWrap: "anywhere",
              }}
            >
              {disease}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AudioItem;